import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";

// Combobox buscable y reutilizable (cliente, crédito, etc.).
// Guarda la referencia real (id) vía onSelect(item); muestra etiqueta legible.
const normalizar = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

export function ComboSelect({
  items = [],
  value,
  onSelect,
  getKey = (it) => it.id,
  getLabel = (it) => it.nombre,
  getSub = () => "",
  getSearch = (it) => it.nombre || "",
  placeholder = "Seleccionar...",
  searchPlaceholder = "Buscar...",
  emptyText = "Sin resultados.",
  fallbackLabel = "",
  disabled = false,
  testid,
  // Búsqueda remota opcional (jul-2026): notifica cada cambio del texto del buscador para
  // que el padre consulte el backend (?search=) y agregue resultados a `items`. El filtro
  // local sigue operando sobre la lista combinada.
  onQueryChange,
}) {
  const [open, setOpen] = useState(false);
  const selected = items.find((it) => getKey(it) === value);
  const label = selected ? getLabel(selected) : (fallbackLabel || "");

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          disabled={disabled}
          data-testid={testid}
          className={cn("h-9 w-full justify-between font-normal", !label && "text-muted-foreground")}
        >
          <span className="truncate">{label || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command
          filter={(val, search) => (normalizar(val).includes(normalizar(search)) ? 1 : 0)}
        >
          <CommandInput placeholder={searchPlaceholder} data-testid={testid ? `${testid}-input` : undefined}
            onValueChange={(q) => onQueryChange?.(q)} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {items.map((it) => {
                const key = getKey(it);
                const sub = getSub(it);
                return (
                  <CommandItem
                    key={key}
                    value={`${getSearch(it)} ${key}`}
                    data-testid={testid ? `${testid}-opt-${key}` : undefined}
                    onSelect={() => { onSelect(it); setOpen(false); }}
                  >
                    <Check className={cn("mr-2 h-4 w-4", value === key ? "opacity-100" : "opacity-0")} />
                    <div className="min-w-0">
                      <p className="truncate text-sm">{getLabel(it)}</p>
                      {sub && <p className="truncate text-xs text-slate-400">{sub}</p>}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
